import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import Button from 'components/field/Button';
import EnumSelect from 'components/field/EnumSelect';
import StringInput from 'components/field/StringInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import ConfirmationModal from 'components/global/ConfirmationModal';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import { usePrompt } from 'hooks/usePrompt';

import { ENUMS, SHIPPING_PARTNERS } from 'services/consts';

import AppLogger from 'utils/AppLogger';

import { faPaperPlaneTop } from '@fortawesome/pro-solid-svg-icons';

const SendDCPickupLocationEmailForm = NiceModal.create(({ selectedDCPickupLocation, dcPickupLocationIds }) => {
	const axios = useAxiosPrivate();
	const { showError, showSuccess } = useAlert();
	const modal = useModal();

	const defaultColSpan = {
		md: 6
	};

	const [isLoading, setIsLoading] = useState(false);

	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					emailAddress: yup.string().email().nullable().required(),
					recipientName: yup.string().nullable().required(),
					language: yup.string().nullable().required()
				})
				.required(),
		[]
	);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			dcPickupLocationIDs: dcPickupLocationIds.join(', '),
			emailAddress: null,
			recipientName: null,
			language: null
		}
	});

	const {
		handleSubmit,
		setValue,
		watch,
		formState: { isDirty, isSubmitSuccessful, isSubmitting }
	} = formMethods;

	usePrompt(isDirty && !isSubmitSuccessful && !isSubmitting);

	const onSubmit = async (data) => {
		setIsLoading(true);
		try {
			const response = await axios.post(SHIPPING_PARTNERS.SEND_DC_PICKUP_LOCATION_EMAIL, {
				ids: selectedDCPickupLocation,
				emailAddress: data.emailAddress,
				recipientName: data.recipientName,
				language: data.language
			});
			if (response.status === 200) {
				showSuccess();
			}
		} catch (error) {
			AppLogger.error(error);
			showError();
		} finally {
			setIsLoading(false);
			modal.remove();
		}
	};

	return (
		<Modal maxWidth="medium" fullwidth className="modal-sendDCPickupLocationEmail">
			<ModalTitle>
				<Typography variant="p1">sendDCPickupLocationEmailForm</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					<FormItem label="dcPickupLocationIDs" colSpan={12}>
						<StringInput name="dcPickupLocationIDs" disabled={true} />
					</FormItem>

					<FormItem colSpan={defaultColSpan} label="emailAddress" required>
						<StringInput name="emailAddress" />
					</FormItem>

					<FormItem colSpan={defaultColSpan} label="recipientName" required>
						<StringInput name="recipientName" />
					</FormItem>

					<FormItem label="language" colSpan={defaultColSpan} required>
						<EnumSelect
							searchable={false}
							valueRemovable={false}
							enumName={ENUMS.LANGUAGE_CODES}
							name="language"
							value={watch(`language`)}
							setValue={(value) => {
								setValue(`language`, value ?? null, { shouldValidate: true, shouldDirty: true });
							}}
						/>
					</FormItem>
				</Form>
			</ModalContent>
			<ModalActions>
				<Button
					label="cancel"
					onClick={() => {
						if (isDirty && !isSubmitSuccessful && !isSubmitting) {
							NiceModal.show(ConfirmationModal, {
								yesAction: () => {
									modal.remove();
								}
							});
						} else {
							modal.remove();
						}
					}}
					variant="outline"
				/>
				<Button
					onClick={handleSubmit(onSubmit, (errors) => {
						AppLogger.log(errors);
					})}
					icon={faPaperPlaneTop}
					label="send"
					loading={isLoading}
					variant="blue"
				/>
			</ModalActions>
		</Modal>
	);
});

export default SendDCPickupLocationEmailForm;
